
import React from 'react';
import {
  PlusIcon,
  XMarkIcon,
  MapPinIcon,
  CheckCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const inputClass =
  'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:bg-gray-100 disabled:text-gray-500';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

// signer: { key, employeeId, cf, name, surname, email, cellNumber, descrizione, isEmployee, locked? }
// positions: [{ pag, x, y, pageH }]
export default function FirmeSignerCard({ signer, title, positions = [], isActive, onChange, onRemove, onActivate, onRemovePosition }) {
  const id = (field) => `firme-signer-${signer.key}-${field}`;

  const field = (name, label, type = 'text') => {
    const locked = name === 'cf';

    return (
      <div className="min-w-0">
        <label className={labelClass} htmlFor={id(name)}>
          {label}
          <span className="ml-1 font-semibold text-red-500" aria-hidden="true">*</span>
          {locked && <LockClosedIcon className="ml-1 inline-block h-3 w-3 text-gray-400" />}
        </label>
        <input
          id={id(name)}
          type={type}
          value={signer[name] || ''}
          readOnly={locked}
          disabled={locked}
          onChange={(e) => onChange(name, e.target.value)}
          className={`${inputClass} ${locked ? 'bg-gray-50 text-gray-500' : ''}`}
        />
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* INTESTAZIONE FIRMATARIO */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {title}
          </h4>

          {signer.name && signer.surname && (
            <p className="mt-0.5 truncate text-xs text-gray-500">
              {signer.name} {signer.surname}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {signer.employeeId && (
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" title="Dipendente selezionato" />
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              title="Rimuovi firmatario"
              aria-label="Rimuovi firmatario"
              className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* DATI FIRMATARIO */}
      <div className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2">
        {field('cf', 'Codice fiscale')}
        {field('email', 'Email', 'email')}
        {field('cellNumber', 'Cellulare per ricezione OTP', 'tel')}
        {field('name', 'Nome')}
        {field('surname', 'Cognome')}
        <div className="sm:col-span-2">
          {field('descrizione', 'Descrizione della firma')}
        </div>
      </div>

      {/* POSIZIONI FIRMA */}
      <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-4 w-4 text-indigo-600" />
            <span className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
              Posizioni firma
            </span>

            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">
              {positions.length}
            </span>
          </div>

          <button
            type="button"
            disabled={!signer.employeeId}
            onClick={onActivate}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              isActive
                ? 'bg-teal-600 text-white hover:bg-teal-500'
                : 'bg-indigo-600 text-white hover:bg-indigo-500'
            }`}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {positions.length ? 'Aggiungi posizione' : 'Posiziona firma'}
          </button>
        </div>

        {positions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {positions.map((position, index) => (
              <div
                key={index}
                className="group inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-white py-1.5 pl-2.5 pr-1 shadow-sm"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded bg-indigo-100 text-[11px] font-bold text-indigo-700">
                  {index + 1}
                </span>

                <span className="whitespace-nowrap text-xs font-medium text-gray-700">
                  Pag. {position.pag}
                </span>

                <button
                  type="button"
                  onClick={() => onRemovePosition?.(index)}
                  title={`Elimina posizione ${index + 1}`}
                  aria-label={`Elimina posizione ${index + 1}`}
                  className="ml-0.5 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">
            Nessuna firma posizionata. Seleziona il firmatario e clicca sul documento PDF.
          </p>
        )}
      </div>
    </div>
  );
}
